'use client';

import * as datadogRum from 'lib/telemetry/datadogRum';
import config from 'utils/config';

declare global {
    interface Window {
        protect?: {
            init: (
                protectApiToken: string,
                options: { onSessionLoad: (deviceSessionId: string | null) => void },
            ) => void;
            identify: (clientUserId: string) => void;
        };
    }
}

const PROTECT_SCRIPT_ID = 'plaid-protect-sdk';
const PROTECT_SCRIPT_SRC = 'https://cdn.plaid.com/protect/stable/protect.umd.js';
const PROTECT_SCRIPT_LOAD_TIMEOUT_MS = 10_000;
const PROTECT_SESSION_DEADLINE_MS = 5_000;

function recordProtectEvent(
    name:
        | 'identified'
        | 'script_error'
        | 'script_timeout'
        | 'session_unavailable'
        | 'session_deadline'
        | 'identify_failed',
    attrs: Record<string, string | number | boolean> = {},
): void {
    datadogRum.addAction(`plaidProtect.${name}`, attrs);
}

let scriptPromise: Promise<void> | null = null;
let sessionPromise: Promise<string> | null = null;
let identifiedUserId: string | null = null;

function loadScript(): Promise<void> {
    scriptPromise ??= new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = PROTECT_SCRIPT_ID;
        script.src = PROTECT_SCRIPT_SRC;
        script.async = true;

        let settled = false;
        const fail = (reason: 'script_error' | 'script_timeout'): void => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            script.remove();
            // A blocked or stalled bundle must not poison a later deposit attempt.
            scriptPromise = null;
            recordProtectEvent(reason);
            reject(new Error(reason));
        };
        const timeoutId = window.setTimeout(() => fail('script_timeout'), PROTECT_SCRIPT_LOAD_TIMEOUT_MS);

        script.onload = (): void => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            resolve();
        };
        script.onerror = (): void => fail('script_error');

        document.body.append(script);
    });

    return scriptPromise;
}

function startSession(protectApiToken: string): Promise<string> {
    sessionPromise ??= loadScript()
        .then(
            () =>
                new Promise<string>((resolve, reject) => {
                    const protect = window.protect;
                    if (!protect) {
                        reject(new Error('session_unavailable'));
                        return;
                    }

                    protect.init(protectApiToken, {
                        onSessionLoad: (deviceSessionId) => {
                            // The SDK reports a failed or timed-out init as a null id.
                            if (!deviceSessionId) {
                                reject(new Error('session_unavailable'));
                                return;
                            }
                            resolve(deviceSessionId);
                        },
                    });
                }),
        )
        .catch((error: unknown) => {
            recordProtectEvent('session_unavailable', { detail: String(error) });
            sessionPromise = null;
            throw error;
        });

    return sessionPromise;
}

function identifyOnce(userId: string, deviceSessionId: string): void {
    if (identifiedUserId === userId) return;
    try {
        window.protect?.identify(userId);
        identifiedUserId = userId;
        recordProtectEvent('identified', { deviceSessionId });
    } catch (error: unknown) {
        recordProtectEvent('identify_failed', { detail: String(error) });
    }
}

/**
 * Plaid Protect device-intelligence facade. Plaid ties the Protect session to a Guarantee
 * authorization through the `client_user_id` the SDK is identified with, which is the same
 * Kalshi user id the backend already puts on the Plaid Item. Nothing crosses our own API:
 * the identify call is the integration, and the device session id stays client-side as a
 * RUM attribute for support.
 *
 * Await this before a deposit request, so Protect has identified the session before the
 * backend authorizes the transfer. It settles at the deadline whatever happens, so an
 * unconfigured token, a blocked bundle or a slow session never holds up a deposit. A late
 * session still identifies itself, because identify is chained on the session rather than
 * on this caller's result.
 */
export async function ensurePlaidProtectIdentified(userId: string): Promise<void> {
    if (typeof window === 'undefined') return;

    const protectApiToken = config.plaidProtectApiToken;
    if (!protectApiToken || !userId) return;

    const session = startSession(protectApiToken).then((deviceSessionId) => {
        identifyOnce(userId, deviceSessionId);
    });

    let deadlineId: number | undefined;
    try {
        await Promise.race([
            session.catch(() => undefined),
            new Promise<void>((resolve) => {
                deadlineId = window.setTimeout(() => {
                    recordProtectEvent('session_deadline');
                    resolve();
                }, PROTECT_SESSION_DEADLINE_MS);
            }),
        ]);
    } finally {
        if (deadlineId !== undefined) window.clearTimeout(deadlineId);
    }
}

/**
 * Starts the Protect session ahead of the deposit request so the SDK has the whole
 * amount-entry and review time to initialize and collect signals.
 */
export function warmPlaidProtectSession(userId: string): void {
    void ensurePlaidProtectIdentified(userId);
}
